// Packages
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import DayPicker, { DateUtils } from "react-day-picker";
import MomentLocaleUtils from "react-day-picker/moment";
import DOMPurify from "dompurify";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const WEEK_DAY_ENUM = Object.freeze({
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
});

function DateField(props) {
  const {
    label,
    required,
    id,
    position,
    value,
    options,
    custom_field_id,
    errors,
    setCustomField,
    custom,
    is_date_restricted,
    scheduling_values_weekly,
    scheduling_values_order,
    scheduling_values_date,
  } = props;
  const state = useSelector((state) => state);
  const containerRef = useRef(null);
  const elementId = `custom_field_${props.area}_${custom_field_id}`;
  const { t } = useTranslation();
  const doingRequest = useSelector((state) => state.ui.doingRequest);
  const store_timezone = useSelector(
    (state) => state.checkoutStore.information.store_timezone,
  );
  const [dateOpen, setDateOpen] = useState(false);

  function isTimeAfter(compareHour, compareMinute) {
    const pad = (n) => String(n).padStart(2, "0");

    const now = new Date();
    const currentTimeStr = now.toTimeString().slice(0, 8);
    const compareTimeStr = `${pad(compareHour)}:${pad(compareMinute)}:00`;

    return currentTimeStr > compareTimeStr;
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setDateOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const checkWeek = (date, weekday) => {
    if (date.getDay() != WEEK_DAY_ENUM[weekday.weekday]) return false;
    if (new Date().toDateString() != date.toDateString()) return false;
    return isTimeAfter(weekday.hours, weekday.minutes);
  };

  const checkOrder = (date, order) => {
    let today = new Date();
    today.setDate(today.getDate() + order.days);
    if (date.toDateString() != today.toDateString()) return false;
    return isTimeAfter(order.hours, order.minutes);
  };

  const checkDate = (date, schedule_date) => {
    if (date.toDateString() != new Date(schedule_date.date).toDateString())
      return false;
    return true;
  };

  const handleDisabledDays = (day) => {
    if (DateUtils.isPastDay(day) && is_date_restricted) {
      return true;
    }
    var available = false;
    scheduling_values_weekly.forEach((weekday) => {
      if (checkWeek(day, weekday)) {
        available = true;
      }
    });
    scheduling_values_order.forEach((order) => {
      if (checkOrder(day, order)) {
        available = true;
      }
    });
    scheduling_values_date.forEach((schedule_date) => {
      if (checkDate(day, schedule_date)) {
        available = true;
      }
    });
    return available;
  };
  return (
    <div className={`col-12 mb-3 form-group`}>
      <div
        className={`input-placeholder floating-label-wrap ${
          errors[elementId] ? "error-div-highlight" : ""
        }`}
      >
        <label htmlFor={elementId} className="label-checkout">
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(label, {
                ADD_ATTR: ["target"],
              }).replace(/<\/?p>/g, ""),
            }}
          />
          {required ? " *" : ""}
        </label>
        <input
          type="text"
          className="floating-label-field floating-label-field--s1 date-picker"
          disabled={doingRequest}
          id={elementId}
          placeholder={label}
          onChange={() => {}}
          onClick={() => {
            setDateOpen(true);
          }}
          value={value}
          min={
            is_date_restricted
              ? new Date().toLocaleDateString("en-CA", {
                  timeZone: store_timezone,
                })
              : undefined
          } // Set min attribute to today's date
        />
      </div>
      {dateOpen && (
        <div
          className="dropdown-menu row px-0 m-0 day-picker-dropdown d-block"
          ref={containerRef}
        >
          <DayPicker
            className="row px-0 mx-0 w-100 Range"
            onDayClick={(date, { disabled }) => {
              if (disabled) return;
              setDateOpen(false);
              setCustomField(
                elementId,
                custom_field_id,
                date.toISOString().split("T")[0],
                props.area,
              );
            }}
            disabledDays={handleDisabledDays}
            selectedDays={state.checkoutStore.shipping.date}
            localeUtils={MomentLocaleUtils}
          />
        </div>
      )}
      <div
        className={`field-error ${errors[elementId] ? "" : "hidden-error"}`}
        style={{
          display: errors[elementId] ? "flex" : "",
          alignItems: "center",
        }}
      >
        <FontAwesomeIcon
          icon={faCircleExclamation}
          style={{ paddingRight: "2px" }}
          size="sm"
          fixedWidth
        />
        {errors[elementId] && t(errors[elementId])}
      </div>
    </div>
  );
}

export default DateField;
